import { useEffect, useState, useRef } from "react"
import { Button } from "@/components"
import { cn } from "@/lib/utils"
import DownloadButton from "../../components-legacy/DownloadButton.jsx"
import Loader from "../../components-legacy/Loader.jsx"
import Popover from "../../components-legacy/Popover.jsx"
import { formatDateTimeText } from "../../system/util.js"
import StrategyBacktestParameters from "./StrategyBacktestParameters.jsx"
import StrategyResultsDeals from "./StrategyResultsDeals.jsx"
import StrategyResultsMain from "./StrategyResultsMain.jsx"
import StrategyResultsOrders from "./StrategyResultsOrders.jsx"
import { StrategyResultsResults } from "./StrategyResultsResults.jsx"
import CircleTick from '../../assets/svg/circle-tick.svg?react'
import { Dialog } from "../../components-legacy/Dialog.jsx"
import AlgoBuilderLogo from '../../assets/svg/ab-logo.svg?react'
import TerminalIcon from '../../assets/svg/terminal.svg?react'
import CloseIcon from '../../assets/svg/close-cross.svg?react'
import InstallGuide from '../../assets/img/install-guide.png'

export default function StrategyResults({ sid, backtest }) {
    const [tab, setTab] = useState('main')
    const [isPopoverParametersVisible, setPopoverParametersVisible] = useState(false)
    const [playViewActive, setPlayViewActive] = useState(false)
    const confirmCancelRef = useRef(null)
    const [isCompletedBannerVisible, setIsCompletedBannerVisible] = useState(true)

    const handleCompletedBannerClose = () => {
        setIsCompletedBannerVisible(false)
    }

    useEffect(() => {
        if (playViewActive) {
            const timer = setTimeout(() => {
                setPlayViewActive(false)
            }, 100)

            return () => clearTimeout(timer)
        }
    }, [playViewActive])

    if (backtest === null) {
        return (
            <section className="p-6 rounded-md min-w-[320px] border border-border bg-card text-foreground text-body">
                No backtest generated
            </section>
        )
    }

    if (backtest === false) {
        return <Loader />
    }

    let statusMsg = '', backtestTxtStatus = null, progressStep = null
    if (backtest.processingStatus === 'processing') {
        statusMsg = 'Processing'
        if (backtest.processing == 0) {
            backtestTxtStatus = 'Preparing backtest ...'
        } else if (backtest.processing == 1) {
            backtestTxtStatus = 'Backtest in progress ...'
            progressStep = 1
        } else if (backtest.processing == 2) {
            backtestTxtStatus = 'Backtest finished. Preparing reports ...'
            progressStep = 2
        }
    } else if (backtest.processingStatus === 'completed') {
        statusMsg = 'Completed'
    } else if (backtest.processingStatus === 'error') {
        statusMsg = 'Error'
    }

    const isCompleted = backtest.processingStatus === 'completed'

    return (
        <>
            <section className="flex flex-wrap gap-2 items-center justify-between relative">
                <div>
                    <div className="text-body text-foreground">
                        <span>Backtest for</span>
                        <span className="font-semibold ml-1">{backtest.inputs.symbol} ({backtest.inputs.period}) </span>
                        <span className="font-semibold mr-1">&ndash; {formatDateTimeText(backtest.created)}</span>
                        <a
                            href="#"
                            onClick={e => { e.preventDefault(); setPopoverParametersVisible(true) }}
                            className="text-accent-blue underline"
                        >
                            more
                        </a>
                        <Popover isVisible={isPopoverParametersVisible} hidePopover={() => setPopoverParametersVisible(false)} position="right" offsetY={30}>
                            <StrategyBacktestParameters backtest={backtest} statusMsg={statusMsg} />
                        </Popover>
                    </div>
                </div>
                <div className="flex gap-2">
                    <DownloadButton strId={sid} title="Download EA" tooltip="Download the EA code of your strategy" buttonDisabled={!isCompleted} />
                    <Button
                        size="sm"
                        variant={isCompleted ? 'default' : 'outline'}
                        className="text-label"
                        disabled={!isCompleted}
                        onClick={() => confirmCancelRef.current.open()}
                    >
                        Install Guide
                    </Button>

                    <Dialog ref={confirmCancelRef}>
                        <InstallationGuidePopup onClose={() => confirmCancelRef.current.close()} />
                    </Dialog>
                </div>
            </section>

            {backtest.processingStatus === 'processing' && (
                <section className="rounded-md px-4 py-4 mt-2 mb-4 border border-border bg-card text-foreground">
                    <div className="flex w-full items-center flex-wrap gap-3">
                        <div className="flex-grow flex gap-2 items-center min-w-[320px]">
                            <span className="inline-block size-2 rounded-full bg-accent-blue animate-pulse" aria-hidden="true" />
                            <div className="text-body">{backtestTxtStatus}</div>
                        </div>
                        {progressStep && (
                            <div className="basis-[400px] grow-0 shrink">
                                <ProgressBar step={progressStep} />
                            </div>
                        )}
                    </div>
                </section>
            )}

            {(isCompleted && backtest.isMessage) && (
                isCompletedBannerVisible && (
                    <section className="rounded-md px-4 py-4 mt-2 mb-4 border border-success-dim bg-card text-foreground">
                        <div className="flex w-full flex-wrap gap-y-6 gap-x-2">
                            <div className="flex-auto flex gap-4 items-center min-w-[320px]">
                                <div className="flex items-center gap-2 flex-auto">
                                    <CircleTick className="text-success shrink-0" />
                                    <div className="text-body-strong">Backtest completed!</div>
                                </div>
                                <div className="flex-auto flex gap-2 items-center">
                                    <div className="w-full flex items-center gap-2">
                                        <ProgressBarStatic value={100} done />
                                        <small className="text-caption text-muted-foreground">100%</small>
                                    </div>
                                </div>
                                <CloseIcon
                                    className="cursor-pointer text-muted-foreground hover:text-foreground"
                                    width={14}
                                    height={14}
                                    onClick={handleCompletedBannerClose}
                                />
                            </div>
                        </div>
                    </section>
                )
            )}


            {backtest.processingStatus === 'error' && (
                <section className="rounded-md px-4 flex justify-center py-2 mt-2 mb-4 border border-danger-dim bg-danger-dim text-danger text-body">
                    <div className="flex gap-2 items-center">Backtest error: Please check the backtest settings.
                    </div>
                </section>
            )}

            {backtest.processingStatus !== 'error' && (
                <>
                    <section className="flex gap-4 mt-4 flex-col md:flex-row">
                        <div className="flex-1 basis-[70%] min-w-0">
                            <StrategyResultsMain strId={sid} backtest={backtest} playViewActive={playViewActive} />
                        </div>
                    </section>

                    <section>
                        <div className="flex gap-2 mb-6">
                            <SubTab active={tab === 'main'} onClick={() => setTab('main')}>Results & Export</SubTab>
                            <SubTab active={tab === 'orders'} onClick={() => setTab('orders')}>Orders</SubTab>
                            <SubTab active={tab === 'deals'} onClick={() => setTab('deals')}>Deals</SubTab>
                        </div>
                        <div className="content">
                            {tab === 'main' && <StrategyResultsResults strId={sid} backtest={backtest}/>}
                            {tab === 'orders' && <StrategyResultsOrders strId={sid} backtest={backtest}/>}
                            {tab === 'deals' && <StrategyResultsDeals strId={sid} backtest={backtest}/>}
                        </div>
                    </section>
                </>
            )}
        </>
    )
}

function SubTab({ active, onClick, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "px-3 py-1.5 rounded-md text-label transition-colors cursor-pointer",
                active
                    ? "bg-primary text-primary-foreground"
                    : "border border-border text-foreground hover:bg-hover"
            )}
        >
            {children}
        </button>
    )
}

function ProgressBarStatic({ value, done }) {
    const pct = Math.max(0, Math.min(100, value))
    return (
        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
            <div
                className={cn("h-full rounded-full transition-all", done ? "bg-success" : "bg-primary")}
                style={{ width: `${pct}%` }}
            />
        </div>
    )
}

const steps = {
    1: { bounds: [0, 85], timeSeconds: 3 * 60 },
    2: { bounds: [85, 99], timeSeconds: 30 }
}

function ProgressBar({ step }) {

    const [current, setCurrent] = useState(0)
    const [data, setData] = useState()

    useEffect(() => {
        setData(steps[step])
    }, [step])

    useEffect(() => {

        if (!data) {
            return
        }

        if (data.bounds[1] >= 100) {
            setCurrent(100)
            return
        }

        if (current >= data.bounds[1]) {
            return
        }

        if (current < data.bounds[0]) {
            setCurrent(data.bounds[0])
            return
        }

        const rate = data.timeSeconds / (data.bounds[1] - data.bounds[0])

        const handle = setTimeout(() => {
            setCurrent(current + 1)
        }, 1000 * rate)

        return () => {
            clearTimeout(handle)
        }
    }, [current, data])

    return (
        <div className="flex gap-2 items-center">
            <ProgressBarStatic value={current} done={current >= 100} />
            <small className="text-caption text-muted-foreground">{current}%</small>
        </div>
    )
}


function InstallationGuidePopup({ onClose }) {
    return (
        <section className="relative rounded-lg max-w-[50rem] p-4 lg:p-8 pb-8 bg-card text-foreground">
            <div className="w-full font-sans">
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
                    aria-label="Close"
                ><CloseIcon /></button>

                <div className="flex items-center gap-3"><AlgoBuilderLogo /></div>

                <div className="mt-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md border border-border text-foreground text-label">
                        <TerminalIcon className="size-4 text-primary" />
                        <span>Installation Guide</span>
                    </div>

                    <h1 className="text-heading lg:text-display mt-4 max-w-3xl text-foreground">
                        How to install and run Expert Advisors (EAs) on the MetaTrader 5 platform
                    </h1>

                    <div className="mt-10 grid md:grid-cols-2 gap-12 items-start">
                        <div className="space-y-8 text-body text-muted-foreground">
                            <div className="flex gap-1">
                                <div>1.</div>
                                <div>Download your Expert Advisor from the AlgoBuilder App. AlgoBuilder&apos;s EAs are .mq5 files.</div>
                            </div>
                            <p className="flex gap-1">
                                <span>2.</span>
                                <span>Open your MT5 platform, then go to File {'>'} Open File Data and in the new Window go into MQL5 {'>'} Experts {'>'} Advisors folder. Copy and paste the EA file into this folder.</span>
                            </p>
                            <p className="flex gap-1">
                                <span>3.</span>
                                <span>Then restart your MT5 platform, or refresh your Navigator window in MT5.</span>
                            </p>
                        </div>

                        <div className="text-caption">
                            <img src={InstallGuide} alt="Installation guide diagram" />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
